import { AppShellMain, Text, ActionIcon, Group, Menu, Flex, AppShell, Button } from "@mantine/core"
import { useContext, useEffect, useState, useCallback } from "react"
import { NBAInsightsContext } from "../context/global-state"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { Playlist as TPlaylist, VideoEvent } from "../client"
import { deletePlaylist, getPublicPlaylists, getUserPlaylists, updatePlaylist } from "../api/Playlists"
import { useAuth0 } from "@auth0/auth0-react"
import VideoDisplay from "../components/VideoDisplay/VideoDisplay"
import { IconArrowLeft, IconDotsVertical, IconDownload, IconEdit, IconLock, IconShare2, IconTrash, IconWorld } from "@tabler/icons-react"
import FocusedVideoDisplay from "../components/VideoDisplay/FocusedVideoDisplay"
import { NBAInsightsHeader } from "../components/Header/Header"
import PageLoader from "../components/Common/PageLoader"

const Playlist: React.FC = () => {
  // Layout info
  // const pinned = useHeadroom({ fixedAt: 120 });
  
  const { isLoading, isAuthenticated, getAccessTokenSilently } = useAuth0()
  const { setUserPlaylists, setPublicPlaylists } = useContext(NBAInsightsContext)
  
  const { id } = useParams();
  const navigate = useNavigate()
  const location = useLocation()
  const [loading, setLoading] = useState<boolean>(true)
  const [fetchError, setFetchError] = useState<boolean>(false)
  const [playlist, setPlaylist] = useState<TPlaylist>()
  const [selectedVideo, setSelectedVideo] = useState<VideoEvent | null>(null)

  const fetchPlaylistData = useCallback(async () => {
    setLoading(true)
    setFetchError(false)

    if (isAuthenticated) {
      try {
        const token = await getAccessTokenSilently()
        const list = await getUserPlaylists(token)
        if (list === null) {
          setFetchError(true)
          setLoading(false)
          return
        }
        const matchingPlaylist = list.find(p => p.id === id)
        if (matchingPlaylist) {
          setPlaylist(matchingPlaylist)
          setLoading(false)
          return
        }
      } catch {
        // Fall back to public playlist check
      }
    }

    const pubList = await getPublicPlaylists()
    if (pubList === null) {
      setFetchError(true)
      setLoading(false)
      return
    }

    setPlaylist(pubList.find(p => p.id === id))
    setLoading(false)
  }, [getAccessTokenSilently, id, isAuthenticated])

  useEffect(() => {
    fetchPlaylistData()
  }, [fetchPlaylistData])


  const handleOnSelect = async (video: VideoEvent) => {
    setSelectedVideo(video)
  }

  const handleDeletePlaylist = async () => {
    if (!playlist) {
      return navigate('/playlists')
    }

    navigate('/playlists')
    const token = await getAccessTokenSilently()
    await deletePlaylist(playlist, token)
    setUserPlaylists(prev => prev.filter(p => p.id !== playlist.id))
    setPublicPlaylists(prev => prev.filter(p => p.id !== playlist.id))
    setPlaylist(undefined)
  }

  const handleEditPlaylist = async () => {
    if (!playlist) {
      return navigate('/playlists')
    }

    navigate(`/modals/edit/${playlist.id}`, {
      state: { previousLocation: location }
    })
  }

  const handleSharePlaylist = async () => {
    navigate('/modals/share', {
      state: { previousLocation: location, url: window.location.href }
    })
  }

  const toggleVisibility = async () => {
    if (!playlist) {
      return navigate('/playlists')
    }

    const token = await getAccessTokenSilently()
    const updatedPlaylist = await updatePlaylist(
      playlist.id,
      null,
      null,
      !playlist.private,
      null,
      token
    )

    if (updatedPlaylist) {
      setUserPlaylists(prev => [...prev.filter(p => p.id !== updatedPlaylist.id), updatedPlaylist])
      setPublicPlaylists(prev => [...prev.filter(p => p.id !== updatedPlaylist.id), updatedPlaylist])
      setPlaylist(updatedPlaylist)
    }
  }

  const handleDownloadPlaylist = async () => {
    if (!playlist) {
      navigate('/playlists')
      return
    }
    navigate(`/modals/download/${playlist.id}`, { state: { previousLocation: location } })
  }

  if (loading || isLoading) return <PageLoader />

  if (fetchError) {
    return (
      <AppShell
        padding={"md"}
        header={{ height: 64 }}
      >
        <NBAInsightsHeader />
        <AppShellMain>
          <Flex direction="column" align="center" justify="center" p={50} gap="md" mih="50vh">
            <Text fz="h3" fw={600} ta="center">Unable to load playlist</Text>
            <Text c="dimmed" size="sm" ta="center">
              Could not retrieve this playlist. Please check your connection and try again.
            </Text>
            <Group mt="sm">
              <Button variant="default" onClick={fetchPlaylistData}>
                Retry
              </Button>
              <Button variant="filled" onClick={() => navigate('/playlists')}>
                Back to Playlists
              </Button>
            </Group>
          </Flex>
        </AppShellMain>
      </AppShell>
    )
  }

  if (!playlist) {
    return (
      <AppShell
        padding={"md"}
        header={{ height: 64 }}
      >
        <NBAInsightsHeader />
        <AppShellMain>
          <Flex direction="column" align="center" justify="center" p={50} gap="sm">
            <Group gap="xs">
              <IconLock size={28} />
              <Text fz="h2" fw={600} ta="center">This is a private playlist</Text>
            </Group>
            <Button mt="md" variant="subtle" color="gray" onClick={() => navigate('/playlists')}>
              Back to Playlists
            </Button>
          </Flex>
        </AppShellMain>
      </AppShell>
    )
  }

  return (
    <AppShell
      padding={"md"}
      header={{ height: 64 }}
    >
      <NBAInsightsHeader />
      <AppShellMain>
        <Group w={"100%"} justify={"space-between"} pl={10}>
          <ActionIcon variant="subtle" color="white" onClick={() => navigate('/playlists')} m={15}><IconArrowLeft /></ActionIcon>
          <Text flex={1} ta="center" fz={"h2"}>Playlist: {playlist!.name}</Text>
          <Menu>
            <Menu.Target>
              <ActionIcon variant="subtle" m={15} color="white"><IconDotsVertical /></ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              { isAuthenticated && playlist.isOwner &&
                <Menu.Item leftSection={<IconEdit size={14} />} onClick={handleEditPlaylist}>
                  <Menu.Label c={"white"}>
                      Edit
                  </Menu.Label>
                </Menu.Item>
              }

              {!playlist.private &&
                <Menu.Item leftSection={<IconShare2 size={14} />} onClick={handleSharePlaylist}>
                  <Menu.Label c={"white"}>Share</Menu.Label>
                </Menu.Item>
              }

              { isAuthenticated && playlist.isOwner &&
                <Menu.Item leftSection={<IconWorld size={14} />} onClick={toggleVisibility}>
                  <Menu.Label c={"white"}>{playlist.private ? "Make Public" : "Make Private"}</Menu.Label>
                </Menu.Item>
              }

              { isAuthenticated && ( playlist.isOwner || !playlist.private ) &&
                <Menu.Item leftSection={<IconDownload size={14} />} onClick={handleDownloadPlaylist}>
                  <Menu.Label c={"white"}>Download</Menu.Label>
                </Menu.Item>
              }

              { isAuthenticated && playlist.isOwner &&
                <Menu.Item leftSection={<IconTrash size={14} />} onClick={handleDeletePlaylist}>
                  <Menu.Label c={"white"}>Delete Playlist</Menu.Label>
                </Menu.Item>
              }
            </Menu.Dropdown>
          </Menu>
        </Group>
        {playlist.videos.length === 0 && <Text ta={"center"} fz={"h4"} mt={20}>This playlist is empty</Text>}
        { selectedVideo ? 
          <FocusedVideoDisplay video={selectedVideo} videos={playlist.videos} activeSearch={true} onSelect={setSelectedVideo} onBack={() => setSelectedVideo(null)}/>
        :
          <VideoDisplay action={ playlist.isOwner ? "delete" : "view" } videos={playlist.videos} onSelect={handleOnSelect} />
        }
      </AppShellMain>
    </AppShell>
  )
}

export default Playlist